import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { getHexToRgba } from "@nykaa/payment-ui/src/common/helpers/getHexToRgba";
import { getFormattedDate } from "../logics/src/utils/helpers";
import { DATE_FORMATS, PAYMENT_OFFER_RESPONSE_CODE, platformNames, STORAGE_KEYS } from "constants/index";
import { Font, Theme } from "@nykaa/ui-components/themes/types";
import { PaymentOfferRule } from "../logics/src/utils/@types";
import { PaymentOfferInfoDesktopConfig } from "@nykaa/payment-sdk/src/PaymentOfferInfo/@types";
import { PaymentOfferConfig } from "@nykaa/payment-sdk/src/PaymentOffer/@types";
import { getIcons, ICONS_NAMES } from "helpers/utils/getIcons";
import { VERNACULAR_DEFAULT_MESSAGES } from "constants/vernacularConstants";
import { allTrackingHandlerForApps } from "helpers/utils/trackingHandler";
import { DEVICE_TYPE, TRACKING_EVENTS } from "constants/index";
import { isMobileApp } from "helpers/deviceType";
import {
  trackPaymentOfferClicked,
  trackPaymentOfferWidgetClicked,
  trackRemoveCouponClick,
  trackKeepCouponClick,
} from "layouts/Payment/common/dependencies/tracking";
import { deCollectCouponApi } from "store/payment/services";
import { setStorage } from "helpers/utils/storage";
import { formatNumber } from "helpers/utils/numberFormatter";


interface IUsePaymentOfferConfig {
  offersData: any;
  deviceType: string;
  mainTitleConfig?: {
    font: string;
    color: string;
  };
  theme: Theme;
  platform: string;
  isApp?: boolean;
  visibleOfferBottomSheet?: boolean;
  setVisibleOfferBottomSheet?: any;
  savedPaymentPage?: boolean;
  PaymentOfferRevamp?: {
    experimentEnabled: boolean;
  };
  offerForSpecificMode?: string[] | [];
  userData?: any;
  setLoading?: (isLoading: boolean) => void;
  showOnlyEligibleOffers?: boolean;
}

interface ICardconfig {
  title: string;
  subTitle: string;
  titleFont: Font;
  subTitleFont: Font;
  subTitleColor: string;
  endIcon: string;
  onClick: () => void;
  leadingIcon: string;
  theme: Theme;
  headerTitle?: string;
  headerFont?: Font;
}

interface IpaymentOfferConfig {
  paymentOfferTitle: string;
  paymentOfferTitleFont: Font;
  paymentOfferTitleColor: string;

  //messageCard props
  messageCardHidden: string;
  messageCard: {
    messageCardText: string;
    messageCardLeadingIcon: string;
    messageCardInfo: boolean;
    messageCardFont: Font;
    messageCardColor: string;
  };
  messageCardActionButtonText: string;

  //bank props
  bankConfig: PaymentOfferConfig[];
  theme: any;
  deviceType: string;
  isRightSideBar?: boolean;
  sideBarVisible?: boolean;
  width?: string;
  handleBackButton?: () => void;
  bottomSheetVisible: boolean;
  bottomSheetIcon: string | (() => SVGElement | JSX.Element);
  bottomSheetClose?: () => void;
}

export const usePaymentOfferConfig = ({
  offersData,
  deviceType,
  mainTitleConfig,
  theme,
  platform,
  isApp = false,
  visibleOfferBottomSheet,
  setVisibleOfferBottomSheet,
  savedPaymentPage,
  PaymentOfferRevamp,
  offerForSpecificMode = [],
  userData,
  setLoading,
  showOnlyEligibleOffers,
}: IUsePaymentOfferConfig) => {
  const [visibleOfferList, setVisibleOfferList] = useState(false);
  const [visibleOfferInfo, setVisibleOfferInfo] = useState(false);
  const [offerData, setOfferData] = useState<PaymentOfferRule>({
    offerImageUrl: "",
    name: "",
    offerLongDescription: "",
    minPayableAmount: 0,
    maximumDiscount: 0,
    usagePerCustomer: 0,
    validTillDate: 0,
    discountAmount: 0,
    offerTNC: [],
    termsAndConditionsLink: "",
    finalOrderAmount: 0,
    priority: 0,
  });
  const [offersList, setOfferList] = useState<PaymentOfferConfig[]>([]);
  const [isClickFromCarousel, setIsClickFromCarousel] = useState(false);

  let paymentOffers = offersData;
  const titleLimit = __PLATFORM__ === platformNames.NIKE ? 16 : 25;
  const subTitleLimit = __PLATFORM__ === platformNames.NIKE ? 20 : 25;
  const history = useHistory();
  // const [isRightScrollBarVisible, setIsRightScrollBarVisible] = useState(false);
  const Wallet_Discount_ICON = getIcons(platform, ICONS_NAMES.WALLET_DISCOUNT);
  const interactionLocation = savedPaymentPage ? "savedPayment" : "freshPayment";

  const toggleOfferList = () => {
    if (!visibleOfferList) {
      trackPaymentOfferWidgetClicked({ interactionLocation });
      // app tracking for payment offer widget  click
      allTrackingHandlerForApps({
        event: TRACKING_EVENTS.PAYMENT_OFFER_WIDGET_CLICK,
        offersData,
        isApp,
        interactionLocation,
      });
    }
    setVisibleOfferList(!visibleOfferList);
    if (setVisibleOfferBottomSheet) {
      setVisibleOfferBottomSheet(!visibleOfferList);
    }
  };

  /* eslint-disable no-shadow */
  const toggleOfferInfo = (offer: PaymentOfferRule, isClickFromCarousel = false) => {
    if (isClickFromCarousel) {
      setIsClickFromCarousel(true);
    } else {
      setIsClickFromCarousel(false);
    }

    if (Object.keys(offer)?.length) {
      /**
       * payment offer widget tracking
       */
      if (PaymentOfferRevamp?.experimentEnabled) {
        // for new design
        const isOfferEligible = !offer.validityDTO;
        const paymentOffer = offer.name;
        const paymentOfferAmount = offer.discountAmount;
        const paymentOfferMode = offer.paymentMethod;

        trackPaymentOfferClicked({
          interactionLocation,
          isOfferEligible,
          paymentOffer,
          paymentOfferAmount,
          paymentOfferMode,
        });
      } else {
        // for old design
        trackPaymentOfferClicked({ interactionLocation });
      }

      // app tracking for payment offer click
      allTrackingHandlerForApps({
        event: TRACKING_EVENTS.PAYMENT_OFFER_CLICKED,
        isApp,
        interactionLocation,
      });
    }
    setVisibleOfferInfo(!visibleOfferInfo);
    setOfferData(offer);
  };

  /**
   * function to get the validity config
   */
  const getOfferValidityConfig = ({
    validityDTO,
    discountAmount,
  }: {
    validityDTO: {
      reasonCode: string;
      message: string;
      couponCode: string;
      couponDiscount: number;
    };
    discountAmount: number;
  }) => {
    const config = {
      isDisable: false,
      message: "",
      color: "",
      font: null,
      couponCode: "",
      couponDiscount: 0,
      removeCoupon: () => {},
      footerActionConfig: null,
    };

    const { reasonCode, couponDiscount = 0, couponCode } = validityDTO;
    let handleRemoveCoupon = () => {};
    let isDisable = false;
    let color = theme.colors.primary;
    let footerActionConfig = null;

    let message =
      validityDTO.message?.length > 30
        ? validityDTO.message.substring(0, 30) + "..."
        : validityDTO.message;

    if (reasonCode === PAYMENT_OFFER_RESPONSE_CODE.NYKAA_FUNDED_COUPON_APPLIED) {
      /**
       * for nykaa funded coupon if payment offer discount is greater.
       * Then give option to remove nykaa funded coupon
       */
      if (discountAmount > couponDiscount) {
        message = "Remove coupon to unlock";
        footerActionConfig = {
          primaryButtonText: "Keep Coupon",
          secondaryButtonText: "Remove Coupon",
          primaryButtonAction: () => {
            trackKeepCouponClick({ interactionLocation });
            setVisibleOfferInfo(false);
          },
          secondaryAction: async (e: any) => {
            e.stopPropagation();

            setVisibleOfferInfo(false);
            setVisibleOfferList(false);

            setLoading && setLoading(true);
            /**
             * tracking for removing coupon
             */
            trackRemoveCouponClick({ interactionLocation, couponCode });

            setStorage({
              key: STORAGE_KEYS.REMOVE_COUPON_ID,
              data: {
                couponCode,
                isFullfilled: false,
                message: "Failed to remove coupon, please try again.",
                icon: getIcons(platform, ICONS_NAMES.ALERT_ICON),
              },
            });

            const params = {
              couponCodeList: [couponCode],
              currencyCode: "INR",
              customerDetail: {
                customerId: userData?.id as string,
                isLoyal: userData?.isLoyal as boolean,
                registeredUser: !userData?.isGuest,
                groupId: userData?.groupId,
              },
            };

            /**
             * for removing coupon.
             */
            deCollectCouponApi(params)
              .then(response => {
                /**
                 * after removing coupon successfully
                 * reloading the page.
                 */
                if (response.success) {
                  setStorage({
                    key: STORAGE_KEYS.REMOVE_COUPON_ID,
                    data: {
                      couponCode,
                      isFullfilled: true,
                      message: `${couponCode} coupon has been removed. The payment offer is now applicable.`,
                      icon: getIcons(platform, ICONS_NAMES.SUCCESS_ICON),
                    },
                  });
                }

                window.location.reload();
                setLoading && setLoading(false);
              })
              .catch(() => {
                setLoading && setLoading(false);
                window.location.reload();
              });
          },
          warningText: `You have applied <b style="font-weight:600;">${couponCode}</b> coupon which cannot be combined with this offer.`,
          warningIcon: getIcons(platform, ICONS_NAMES.ALERT_ICON),
        };
      } else {
        isDisable = true;
        message = "Not applicable";
        color = theme.colors.warning;
        footerActionConfig = {
          primaryButtonText: "",
          secondaryButtonText: "Okay",
          primaryButtonAction: () => {},
          secondaryAction: () => {
            setVisibleOfferInfo(false);
          },
          warningText: `You have applied <b style="font-weight:600;">${couponCode}</b> coupon which cannot be combined with this offer.`,
          warningIcon: getIcons(platform, ICONS_NAMES.ALERT_ICON),
        };
      }
    }

    switch (reasonCode) {
      case PAYMENT_OFFER_RESPONSE_CODE.USAGE_EXHAUSTED:
      case PAYMENT_OFFER_RESPONSE_CODE.INSTRUMENT_VALIDATE:
        return {
          isDisable: true,
          message,
          color: theme.colors.warning,
          font: theme.typography.subTitleMedium,
          couponCode,
          couponDiscount,
          handleRemoveCoupon,
          footerActionConfig,
        };
      case PAYMENT_OFFER_RESPONSE_CODE.NYKAA_FUNDED_COUPON_APPLIED:
      case PAYMENT_OFFER_RESPONSE_CODE.MIN_PAYABLE_VALIDATION:
        return {
          isDisable,
          message,
          color,
          font: theme.typography.subTitleMedium,
          couponCode,
          couponDiscount,
          handleRemoveCoupon,
          footerActionConfig,
        };
      default:
        return config;
    }
  };

  useEffect(() => {
    if (!visibleOfferBottomSheet) {
      if (visibleOfferInfo) {
        setVisibleOfferInfo(false);
      }

      if (visibleOfferList) {
        setVisibleOfferList(false);
      }
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [visibleOfferBottomSheet]);

  useMemo(() => {
    const offers: PaymentOfferConfig[] = [];
    let rules: PaymentOfferRule[] = [];
    if (paymentOffers && paymentOffers?.paymentRules && paymentOffers?.nonEligiblePaymentRules) {
      rules = [...paymentOffers?.paymentRules, ...paymentOffers?.nonEligiblePaymentRules];
    }
    rules.forEach((rule: PaymentOfferRule) => {
      if (showOnlyEligibleOffers && rule.validityDTO) {
        return;
      }

      if (rule?.paymentMethod) {
        let validityConfig = null;
        if (rule.validityDTO) {
          validityConfig = getOfferValidityConfig({
            validityDTO: rule.validityDTO,
            discountAmount: rule?.discountAmount || 0,
          });
        }

        const config = {
          leadingIcon: "",
          title: "",
          titleFont: theme.typography.bodySmall,
          titleColor: getHexToRgba(theme.colors.textPrimary, 0.92),
          subTitle: "",
          subTitleFont: theme.typography.subTitleMedium,
          subTitleColor: getHexToRgba(theme.colors.textPrimary, 0.92),
          offerTitle: "",
          offerTitleFont: theme.typography.subTitleSmall,
          offerTitleColor: theme.colors.positive,
          infoTitle: VERNACULAR_DEFAULT_MESSAGES.INFO,
          infoTitleFont: theme.typography.buttonSmall,
          infoTitleColor: (theme?.colors as any)?.textPrimary064,
          negativeTitle: "",
          negativeTitleFont: theme.typography.type121,
          negativeTitleColor: theme.colors.info,
          theme,
          noSidePadding: true,
          onClick: (isClickFromCarousel?: boolean) => {
            toggleOfferInfo({ ...rule, validityDTO: validityConfig }, isClickFromCarousel);
          },
          icon: getIcons(platform, ICONS_NAMES.ACTION_ICON),
          noPadding: true,
          finalOrderAmount: 0,
          validityConfig,
          discountAmount: rule?.discountAmount || 0,
        };
        if (rule?.offerImageUrl) {
          config.leadingIcon = rule.offerImageUrl;
        }
        if (rule?.name) {
          config.title = rule.name.length > titleLimit ? rule.name.substring(0, titleLimit) + "..." : rule.name;
        }
        if (rule?.description) {
          config.subTitle =
            rule.description.length > subTitleLimit
              ? rule.description.substring(0, subTitleLimit) + "..."
              : rule.description;
        }
        if (rule?.genericDiscountMsg) {
          config.offerTitle = rule.genericDiscountMsg;
        }
        if (rule?.deltaAmountMsg) {
          config.negativeTitle = rule.deltaAmountMsg;
        }

        /**
         * To filter out offer for specific payment method.
         */
        if (offerForSpecificMode.length) {
          if (offerForSpecificMode.includes(rule?.paymentMethod)) {
            offers.push(config);
          }
        } else {
          offers.push(config);
        }
      }
    });
    if (offers.length) {
      setOfferList(offers);
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [paymentOffers]);

  const cardConfig: ICardconfig = {
    title: `Save more with ${offersList.length} Offers`,
    subTitle: "Cards, UPI, Net banking etc.",
    titleFont: theme.typography.subTitleLarge,
    subTitleFont: theme.typography.bodyMedium,
    subTitleColor: getHexToRgba(theme.colors.chipColor, 0.64),
    endIcon: getIcons(platform, ICONS_NAMES.ARROW_RIGHT),
    onClick: toggleOfferList,
    leadingIcon: Wallet_Discount_ICON,
    theme,
  };

  let messageCardText = "Payment offer will auto apply when you pay";
  if (PaymentOfferRevamp?.experimentEnabled) {
    messageCardText = "Auto-applied during checkout";
  }

  const paymentOfferConfig: IpaymentOfferConfig = {
    paymentOfferTitle: "Payment Offers",
    paymentOfferTitleFont: mainTitleConfig?.font || theme.typography.titleLarge,
    paymentOfferTitleColor: mainTitleConfig?.color || theme.colors.textPrimary,

    //messageCard props
    messageCardHidden: "false",
    messageCard: {
      messageCardText,
      messageCardLeadingIcon: getIcons(platform, ICONS_NAMES.ALERT_INFO),
      messageCardInfo: true,
      messageCardFont: theme.typography.bodySmall,
      messageCardColor: getHexToRgba(theme.colors.textPrimary, 0.92),
    },
    messageCardActionButtonText: "",

    //bank props
    bankConfig: offersList,
    theme,
    deviceType,
  };

  const paymentOfferInfoConfig: PaymentOfferInfoDesktopConfig = {
    backIcon: getIcons(platform, ICONS_NAMES.CHEVRON),
    icon: offerData?.offerImageUrl,
    title: offerData?.name,
    titleFont: theme.typography.type202,
    subTitle: offerData?.offerLongDescription,
    subTitleFont: theme.typography.type140,
    priceList: [
      {
        label:
          offerData.minPayableAmount && (offerData?.minPayableAmount > 0 ? "Min. order value" : ""),
        value:
          offerData.minPayableAmount &&
          (offerData?.minPayableAmount > 0 ? `₹${formatNumber(offerData?.minPayableAmount)}` : ""),
      },
      {
        label:
          offerData?.maximumDiscount &&
          (offerData?.maximumDiscount > 0 ? "Max. discount/cashback" : ""),
        value:
          offerData?.maximumDiscount &&
          (offerData?.maximumDiscount > 0 ? `₹${formatNumber(offerData?.maximumDiscount)}` : ""),
      },
      {
        label: offerData?.usagePerCustomer && "Max. Usage per account",
        value: offerData?.usagePerCustomer && offerData?.usagePerCustomer,
      },
      {
        label: offerData?.validTillDate && "Valid Till",
        value:
          offerData?.validTillDate &&
          offerData?.validTillDate &&
          getFormattedDate(new Date(offerData?.validTillDate), DATE_FORMATS.PAYMENT_OFFER),
      },
    ],
    totalText: {
      label: offerData?.discountAmount && (offerData?.discountAmount > 0 ? "You get" : ""),
      value:
        offerData?.discountAmount &&
        (offerData?.discountAmount > 0 ? `₹${formatNumber(Math.floor(offerData?.discountAmount))}` : ""),
    },
    tncList: offerData?.termsAndConditionsLink ? offerData?.offerTNC : [],
    tncButtonText: offerData?.termsAndConditionsLink ? "terms & conditions" : "",
    tncButtonAction: () => {
      if (isMobileApp()) {
        window.open(offerData?.termsAndConditionsLink);
      } else {
        window.open(offerData?.termsAndConditionsLink, "_blank");
      }
    },
    /* eslint-disable @typescript-eslint/no-object-literal-type-assertion */
    handleBackButton: () => {
      toggleOfferInfo({} as PaymentOfferRule);
    },
    termsAndConditionText: offerData?.termsAndConditionsLink ? "Click here for offer" : "",
    validityConfig: offerData?.validityDTO,
    theme,
  };

  if (deviceType === DEVICE_TYPE.DESKTOP) {
    paymentOfferConfig.isRightSideBar = true;
    paymentOfferConfig.sideBarVisible = visibleOfferList;
    paymentOfferConfig.width = "400px";
    paymentOfferConfig.handleBackButton = toggleOfferList;

    paymentOfferInfoConfig.isRightSideBar = true;
    paymentOfferInfoConfig.sideBarVisible = visibleOfferInfo;
    paymentOfferInfoConfig.width = "400px";
    /* eslint-disable @typescript-eslint/no-object-literal-type-assertion */
    paymentOfferInfoConfig.handleBackButton = () => toggleOfferInfo({} as PaymentOfferRule);
  } else {
    paymentOfferConfig.bottomSheetVisible = visibleOfferList;
    paymentOfferConfig.bottomSheetIcon = getIcons(platform, ICONS_NAMES.CLEAR);
    paymentOfferConfig.bottomSheetClose = toggleOfferList;
    paymentOfferInfoConfig.bottomSheetVisible = visibleOfferInfo;
    /* eslint-disable @typescript-eslint/no-object-literal-type-assertion */
    paymentOfferInfoConfig.bottomSheetClose = () => toggleOfferInfo({} as PaymentOfferRule);
    paymentOfferInfoConfig.overlayClick = () => {
      /* eslint-disable @typescript-eslint/no-object-literal-type-assertion */
      toggleOfferInfo({} as PaymentOfferRule);
      toggleOfferList();
    };
    cardConfig.headerTitle = "Payment Offers";
    cardConfig.headerFont = theme.typography.sectionTitle;
  }

  // useEffect(() => {
  //   if (!visibleOfferList && !visibleOfferInfo) setIsRightScrollBarVisible(false);
  // }, [visibleOfferList]);

  const otherOffersCardDetails = {
    cardConfig,
    paymentOfferConfig,
    paymentOfferInfoConfig,
    isClickFromCarousel,
    theme,
    history,
  };
  return otherOffersCardDetails;
};
